import { Card, CardContent } from "@/components/ui/card";

export function KPICard({ label, value, hint, icon: Icon, accent = "accent", testid }) {
  return (
    <Card className="overflow-hidden" data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        {Icon && (
          <div className={`h-10 w-10 rounded-md grid place-items-center bg-${accent}/10 border border-${accent}/20`}>
            <Icon className={`h-5 w-5 text-${accent}`} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="text-2xl md:text-3xl font-semibold tabular-nums tracking-tight text-foreground">
            {value}
          </div>
          {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

export function StatusPill({ kind = "info", size = "default", children }) {
  const map = {
    ok: "bg-[hsl(var(--ok))]/15 text-[hsl(var(--ok))] border-[hsl(var(--ok))]/30",
    warn: "bg-[hsl(var(--warn))]/15 text-[hsl(var(--warn))] border-[hsl(var(--warn))]/30",
    danger: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
    info: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30",
    offline: "bg-[hsl(var(--offline))]/15 text-[hsl(var(--offline))] border-[hsl(var(--offline))]/30",
    security: "bg-[hsl(var(--security))]/15 text-[hsl(var(--security))] border-[hsl(var(--security))]/30",
    muted: "bg-muted text-muted-foreground border-border",
  };
  // `xs` is for very compact contexts (e.g. inline alongside icon
  // toggles in dense tables). The default has been tightened slightly
  // so every pill across the app looks more refined without breaking
  // layouts that relied on the old size.
  const sizeCls =
    size === "xs"
      ? "px-1.5 py-px text-[10px] leading-tight"
      : "px-1.5 py-0.5 text-[11px]";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border font-medium ${sizeCls} ${map[kind] || map.info}`}
    >
      {children}
    </span>
  );
}

function _meterColor(pct) {
  return pct >= 95
    ? "bg-[hsl(var(--danger))]"
    : pct >= 75
    ? "bg-[hsl(var(--warn))]"
    : "bg-[hsl(var(--accent))]";
}

function _pct(u, t) {
  return t > 0 ? Math.min(100, Math.round(((u || 0) / t) * 100)) : 0;
}

function _Bar({ used, total, label, size = "sm" }) {
  const pct = _pct(used, total);
  const h = size === "lg" ? "h-2.5" : "h-1.5";
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground tabular-nums">
        <span className="font-mono">
          <span className="text-muted-foreground/90 mr-1">{label}</span>
          {used || 0} / {total || "∞"}
        </span>
        <span>{total ? `${pct}%` : "unlimited"}</span>
      </div>
      <div className={`mt-1 w-full bg-muted rounded-full ${h}`}>
        <div
          className={`${_meterColor(pct)} rounded-full ${h}`}
          style={{ width: `${pct}%`, transition: "width 200ms ease-out" }}
        />
      </div>
    </div>
  );
}

/**
 * ChannelMeter - renders channel usage in three possible layouts.
 *
 *  - policy="total" (default legacy): single bar of `used/total`.
 *  - policy="split": two stacked bars, IN and OUT, each against its own cap.
 *  - policy="both":  three bars — total on top, then IN + OUT sub-caps.
 *
 * Backward compatible: callers not passing `policy` still work with
 * `used`+`total` exactly as before.
 */
export function ChannelMeter({
  used = 0,
  total = 0,
  size = "sm",
  policy = "total",
  used_in = 0,
  used_out = 0,
  max_in = 0,
  max_out = 0,
}) {
  const effectivePolicy =
    policy === "split" || policy === "both" ? policy : "total";

  if (effectivePolicy === "total") {
    const pct = _pct(used, total);
    const h = size === "lg" ? "h-2.5" : "h-1.5";
    return (
      <div className="w-full">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground tabular-nums">
          <span className="font-mono">
            {used} / {total || "∞"}
          </span>
          <span>{total ? `${pct}%` : "unlimited"}</span>
        </div>
        <div className={`mt-1 w-full bg-muted rounded-full ${h}`}>
          <div
            className={`${_meterColor(pct)} rounded-full ${h}`}
            style={{ width: `${pct}%`, transition: "width 200ms ease-out" }}
          />
        </div>
      </div>
    );
  }
  if (effectivePolicy === "split") {
    return (
      <div className="w-full space-y-1.5">
        <_Bar used={used_in} total={max_in} label="IN" size={size} />
        <_Bar used={used_out} total={max_out} label="OUT" size={size} />
      </div>
    );
  }
  // both
  return (
    <div className="w-full space-y-1.5">
      <_Bar used={used} total={total} label="TOT" size={size} />
      <_Bar used={used_in} total={max_in} label="IN" size={size} />
      <_Bar used={used_out} total={max_out} label="OUT" size={size} />
    </div>
  );
}

/**
 * EmptyState — Phase 6n polish.
 *
 * Per design_guidelines §9 this must be minimal, operator-grade: one
 * sentence + one primary CTA, no illustrations. Swapped the oversized
 * 10-padding center-splash for a tighter 6-padding layout that reads as
 * a nudge rather than a wall. Kept centered alignment because lists
 * that disappear mid-workflow read better with the eye landing on the
 * action, not scanning for it on the left.
 */
export function EmptyState({ title, description, action }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-card/50 p-6 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {description && (
        <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto leading-relaxed">
          {description}
        </p>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/**
 * Compact pill showing the channel-policy mode of a tenant/trunk.
 * Optionally appends the relevant cap numbers inline.
 *
 *   policy="total"  -> `• TOTAL 10`
 *   policy="split"  -> `• SPLIT · ↓4 ↑6`
 *   policy="both"   -> `• BOTH · tot 10 · ↓4 ↑6`
 *
 * When the entity is on `total` and the caller sets compact, renders nothing
 * (no noise for the default case).
 */
export function PolicyPill({ entity = {}, totalField = "channels", compact = false }) {
  const policy = entity.channel_policy || "total";
  const lims = entity.limits || entity; // trunks store at top-level
  const total = Number(lims[totalField] ?? entity[totalField] ?? 0);
  const cIn = Number(lims.channels_in ?? entity.channels_in ?? 0);
  const cOut = Number(lims.channels_out ?? entity.channels_out ?? 0);
  if (compact && policy === "total") return null;
  const tone =
    policy === "both"
      ? "border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]"
      : policy === "split"
      ? "border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]"
      : "border-border bg-muted text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-[1px] text-[10px] font-medium uppercase tracking-wider ${tone}`}
      data-testid={`policy-pill-${policy}`}
      title={`channel_policy=${policy}`}
    >
      {policy}
      {policy === "total" && total > 0 && (
        <span className="font-mono normal-case tracking-normal opacity-80">{total}</span>
      )}
      {policy === "split" && (
        <span className="font-mono normal-case tracking-normal opacity-80">
          ↓{cIn} ↑{cOut}
        </span>
      )}
      {policy === "both" && (
        <span className="font-mono normal-case tracking-normal opacity-80">
          {total} · ↓{cIn} ↑{cOut}
        </span>
      )}
    </span>
  );
}

import { HelpHint } from "@/lib/help.jsx";

export function PageHeader({ title, subtitle, actions, icon: Icon, testid, helpKey }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col gap-3 mb-6 pb-4 border-b border-border lg:flex-row lg:items-start lg:justify-between lg:gap-4"
    >
      <div className="flex items-start gap-3 min-w-0">
        {Icon && (
          <div className="h-10 w-10 shrink-0 rounded-md bg-accent/10 border border-accent/20 grid place-items-center">
            <Icon className="h-5 w-5 text-accent" />
          </div>
        )}
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-foreground flex items-center gap-1.5">
            {title}
            {helpKey && <HelpHint i18nKey={helpKey} />}
          </h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap lg:shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
}
